import { BANNER_TAG_LABEL_VARIATIONS } from '@jbhi-fi/lanyard-ui/components/BannerTag/BannerTag';
import { PdpAlert } from '../StoreSettingsProvider/types';
import { DeliveryTag } from '@jbhi-fi/utils/src/getDeliveryTag/getDeliveryTag';

export type BannerLabel = keyof typeof BANNER_TAG_LABEL_VARIATIONS;

export enum StockLevels {
  LastChance = 'LastChance',
  SellingFast = 'SellingFast',
  Gone = 'Gone',
  RunningOut = 'RunningOut',
}

export enum ActionButtonType {
  AddToCart = 'addToCart',
  Buy = 'addToCart',
  View = 'view',
  PreOrder = 'preOrder',
  NotifyMe = 'notifyMe',
  DoNotDisplay = 'doNotDisplay',
  CheckAvailability = 'checkAvailability',
}

export type Category = {
  id: number;
  brandId: number;
  name: string;
  parentCategoryId: number;
};

export type Department = {
  id: number;
  description: string;
};

export type Display = {
  artist: string;
  averageProductReviewRating?: number;
  brandImageUrl: string;
  categoryHierarchy: Array<string>;
  description: string;
  extendedDescription: string;
  format?: string;
  messages?: Array<Message>;
  numberOfProductReviews?: number;
  features?: Array<string>;
  highlight?: string;
  department?: Department;
};

export type Price = {
  sku: string;
  displayPriceAfterCashback: boolean;
  displayPriceExc: number;
  displayPriceInc: number;
  displayPriceLastChangedTime: string;
  displayPriceTax: number;
  displayWasPrice: boolean;
  locationId: number;
  saveAmount: number;
  timestamp: string;
  wasPrice: number;
  wasPriceExpiryDate: string;
  priceAfterCashback: number;
};

export type ProductFlag = {
  id: number;
  name: string;
};

export type Product = {
  brand: string;
  barcode: string;
  campaigns: Array<any>;
  categoryId: number;
  departmentCode: number;
  description: string;
  ean13: string;
  flags: Array<ProductFlag>;
  freightPrice: number;
  hasOnlineCategory: boolean;
  id: string;
  isActive: boolean;
  isDiminishing: boolean;
  isInActive: boolean;
  isOnline: boolean;
  limitPerOrder: number;
  limitedRunPromotionItems: Array<any>;
  model: string;
  notifyMe: boolean;
  onPromotion: boolean;
  packageDimensions: {
    height?: number;
    width?: number;
    depth?: number;
    weight?: number;
  };
  productDimensions: {
    height?: number;
    width?: number;
    depth?: number;
    weight?: number;
  };
  productTag: string;
  releaseDate: string;
  seasonCode: string;
};

export type ProductGroup = {
  id: number;
  description: string;
  timestamp: string;
};

export type ProductTags = {
  googleTagManager?: GoogleTagManager;
  bannerTag?: {
    backgroundColor: string;
    colour: string;
    label: BannerLabel;
  };
  promoTags?: Array<PromoTag>;
};

export type PromoTag = {
  backgroundColor: string;
  colour: string;
  label: string;
};

export type GoogleTagManager = {
  id: string;
  name: string;
  brand: string;
  category: string;
  dimension4: string;
  dimension5: string;
  dimension6: string;
  dimension7: string;
  price: number;
};

export type MediaType = {
  mediaContentType: string;
  alt: string;
  previewImage: {
    url: string;
  };
  id: string;
  image: {
    url: string;
    width: number;
    height: number;
  };
};

export type Availability = {
  sku: string;
  availabilityStatement: string;
  canBuyOnline: boolean;
  canPreOrder: boolean;
  cashNCarryStatus: string;
  clickNCollectStatus: string;
  deliveryConstraint?: string;
  deliveryOnly: boolean;
  deliveryStatus: string;
  displayProduct: boolean;
  fulfilment: string;
  limitedRun?: LimitedRunPromo;
};

export type LimitedRunPromo = {
  promotionId?: number;
  isActive?: boolean;
  stockLevelStatus?: StockLevels;
  stockLevelChangedAt?: string;
};

export type Reviews = {
  numberOfReviews?: number;
  rating?: string | number;
  pctRating?: number;
};

export type ProductMessageChat = {
  label?: string;
  department?: string;
};

export type ProductMessageLinks = {
  label?: string;
  url: string;
};

export type SpecialOffer = {
  title?: string;
  subType?: string;
  content?: string;
  endDate?: string;
  imageUrl?: string;
  disclaimer?: string;
  chat?: ProductMessageChat;
  links?: Array<ProductMessageLinks>;
};
export type InstallationOfferType = {
  heading: string;
  shortDescription?: string;
};

export type AlertType = 'warning' | 'error' | 'info' | 'success' | 'important';

export type ProductMessage = {
  id?: string;
  title?: string;
  content: string;
  imageUrl?: string;
  type: MessageType;
};

export type MessageType = 'Important' | 'Warning' | 'General' | 'Critical' | string;
export type Message = {
  id: string;
  type: 'Messages' | 'SpecialOffers' | 'Services';
  subType: MessageType;
  start?: string;
  finish?: string;
  priority?: number;
  name: string;
  heading: string;
  shortDescription?: string;
  description: string;
  titleImageUrl?: string;
  heroBannerImageUrl?: string;
  messageImageUrl?: string;
};

export type PdpProductType = {
  alerts?: Array<PdpAlert>;
  artist?: string;
  availability: Availability;
  bannerLabel?: BannerLabel;
  brand?: string;
  brandLogo?: string;
  buttonType: ActionButtonType;
  containsHideAwaitingStockFlag: boolean;
  deliveryAmount: number;
  deliveryTag: DeliveryTag;
  descriptionHtml?: string;
  freightPrice?: number;
  gtmProduct?: GoogleTagManager;
  id: string;
  isArtist: boolean;
  isBackOrder: boolean;
  isComingSoon: boolean;
  isDigital: boolean;
  isDigitalPreOrder: boolean;
  isPreOrder: boolean;
  isSoftware: boolean;
  isVariantAvailable: boolean;
  limitedRunPromo?: LimitedRunPromo;
  media?: Array<MediaType>;
  model?: string;
  plu?: string;
  price: Price;
  productPrice: number;
  reviews?: Reviews;
  shouldHidePriceStrikeThrough: boolean;
  shouldShowReviews: boolean;
  shouldDisplayWasPrice: boolean;
  sku: string;
  softwareFormat?: string;
  title: string;
  variantId: string;
  specialOffers?: Array<SpecialOffer>;
  productMessages?: Array<ProductMessage>;
  promoTags?: Array<PromoTag>;
  keyFeatures?: Array<string>;
  highlight?: string;
  shouldHaveExtraDescriptions: boolean;
  releaseDate?: string;
  consumerAdvice?: string;
  ageRating?: string;
};
